import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/api/client';
import { SearchableSelect } from '@/components/ui/SearchableSelect';

export interface AssignableUser { id: number; name: string; empCode?: string | null; userType?: string | null }

/**
 * Active internal users, for the "who owns this merchant" dropdowns.
 * `/users/assignable` returns only id/name/empCode/userType, so it's safe for any internal staff.
 */
export function useAssignableUsers() {
  return useQuery({
    queryKey: ['users-assignable'],
    queryFn: async () => (await api.get<AssignableUser[]>('/users/assignable')).data,
    staleTime: 5 * 60 * 1000,
  });
}

const PRETTY_TYPE: Record<string, string> = {
  SALES_PERSON: 'Sales',
  CUSTOMER_SUPPORT: 'Customer Support',
  SUPER_ADMIN: 'Super Admin',
  ADMIN: 'Admin',
  OPERATION: 'Operations',
  BRANCH_HEAD: 'Branch Head',
};

/**
 * Owner picker for Sales Person / CS Person on a merchant.
 *
 * Every active internal user is selectable (ownership doesn't always follow job title), but users whose
 * userType matches `preferType` are floated to the top so the obvious candidates come first.
 */
export function OwnerSelect({
  value, onChange, preferType, placeholder,
}: {
  value?: number | null;
  onChange: (v: number | null) => void;
  preferType?: 'SALES_PERSON' | 'CUSTOMER_SUPPORT';
  placeholder?: string;
}) {
  const { data: users, isLoading } = useAssignableUsers();

  const options = useMemo(() => {
    const list = users ?? [];
    const rank = (u: AssignableUser) => (preferType && u.userType === preferType ? 0 : 1);
    return [...list]
      .sort((a, b) => rank(a) - rank(b) || a.name.localeCompare(b.name))
      .map((u) => ({
        value: u.id,
        label: u.name,
        hint: [u.empCode, u.userType ? (PRETTY_TYPE[u.userType] ?? u.userType) : null].filter(Boolean).join(' · '),
      }));
  }, [users, preferType]);

  return (
    <SearchableSelect
      value={value ?? ''}
      onChange={(v) => onChange(v === '' || v == null ? null : Number(v))}
      options={options}
      placeholder={isLoading ? 'Loading users…' : (placeholder ?? 'Not assigned')}
    />
  );
}
